import {
  CognitoUserSession,
  CognitoUser,
  CognitoUserPool,
  AuthenticationDetails,
} from 'amazon-cognito-identity-js';
import { Config } from '../types/config';
import { log } from './log';
import { UserRole, User } from '../types/user';
import { Account } from '../types/account';
import { Api } from './api';

const LOG_CHANNEL = 'cognito';

export const COGNITO_ERRORS: { [key: string]: string } = {
  ExpiredCodeException:
    'Ihr Passwort Reset Code ist nicht mehr gültig, bitte fordern sie einen neuen an',
  InvalidParameterException:
    'Das Passwort erfüllt nicht die notwendigen mindestanforderungen. Es muss mindestens 8 Zeichen lang sein.',
  NotAuthorizedException: 'Login fehlgeschlagen',
  UserNotFoundException: 'Benutzer nicht bekannt',
};

interface LoginOptions {
  email: string;
  password: string;
}

export type AuthenticatedUser = {
  userId?: string;
  role?: string;
  accountId?: string;

  //accounts?:Account[]
  managerId?: string;
  systemUser?: User;
  cognitoUser?: CognitoUser;
  session?: CognitoUserSession;
  setNewPassword?: (
    password: string,
    attributesData: {},
  ) => Promise<AuthenticatedUser>;
};

type AuthChangedCallback = (user?: AuthenticatedUser) => void;

class AuthClient {
  userPool: CognitoUserPool;
  session?: CognitoUserSession;
  callbacks: AuthChangedCallback[] = [];
  user?: AuthenticatedUser;
  constructor({
    REACT_APP_COGNITO_POOL_ID,
    REACT_APP_COGNITO_CLIENT_ID,
  }: Config) {
    // console.log('auth','init auth',REACT_APP_COGNITO_POOL_ID,REACT_APP_COGNITO_CLIENT_ID)
    this.userPool = new CognitoUserPool({
      UserPoolId: REACT_APP_COGNITO_POOL_ID,
      ClientId: REACT_APP_COGNITO_CLIENT_ID,
    });
    this.init();
  }
  async init() {
    const user = await this.getUser();
    this.onNotify(user);
  }
  async onUserChanged(cb: AuthChangedCallback) {
    this.callbacks.push(cb);
    //const user = await this.getUser()
    cb(this.user);
  }
  onNotify(user?: AuthenticatedUser) {
    // console.log('notify user',user)
    this.user = user;
    this.callbacks.forEach((cb) => cb(user));
  }
  offUserChanged(cb: AuthChangedCallback) {
    this.callbacks = this.callbacks.filter((fn) => fn !== cb);
  }
  async login({ email, password }: LoginOptions): Promise<AuthenticatedUser> {
    const user = await loginUser(
      { userPool: this.userPool },
      { email, password },
    );
    this.onNotify(user);
    return user;
  }
  async getUser(): Promise<AuthenticatedUser> {
    const user = await getUser({ userPool: this.userPool });
    const systemUser = user.userId ? await Api.getUser(user.userId) : null;
    user.systemUser = systemUser;
    this.user = user;
    return user;
  }
  forgotPassword({ email }: { email: string }) {
    return forgotPassword({ userPool: this.userPool }, { email });
  }
  confirmPassword({
    email,
    password,
    token,
  }: {
    email: string;
    password: string;
    token: string;
  }) {
    return confirmPassword(
      { userPool: this.userPool },
      { email, password, token },
    );
  }
  async logout() {
    await logoutUser({ userPool: this.userPool });
    this.onNotify();
  }
  async getJwtToken(): Promise<String | null> {
    const { session } = this;
    this.session = await getSession({ userPool: this.userPool }, { session });
    return this.session ? this.session.getIdToken().getJwtToken() : null;
  }
}

export const logoutUser = async ({
  userPool,
}: {
  userPool: CognitoUserPool;
}) => {
  const user = await getUser({ userPool });
  if (user && user.cognitoUser) {
    // console.log('logout user')
    user.cognitoUser.signOut();
  }
};
export const getSession = async (
  { userPool }: { userPool: CognitoUserPool },
  { session }: { session?: CognitoUserSession },
): Promise<CognitoUserSession | undefined> => {
  if (!session || !session.isValid()) {
    log(LOG_CHANNEL, 'refreshing session');
    const user = await getUser({ userPool });
    return user.session;
  }
  //log(LOG_CHANNEL,'get session from cache')
  return session;
};
export const getUser = async ({
  userPool,
}: {
  userPool: CognitoUserPool;
}): Promise<AuthenticatedUser> => {
  return new Promise<AuthenticatedUser>((resolve, reject) => {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser != null) {
      cognitoUser.getSession((err: Error, session: CognitoUserSession) => {
        if (err || !session.isValid()) {
          reject(err);
          return;
        }
        cognitoUser.getUserAttributes((err, attributes) => {
          if (err || !attributes) return reject(err);
          // console.log('get attributes', attributes)
          const atts = ['userId', 'accountId', 'managerId', 'role'];
          const authenticateUser = attributes.reduce<AuthenticatedUser>(
            (agg, attribute) => {
              const name = attribute.getName();
              const value = attribute.getValue();
              if (name === 'custom:userId') agg.userId = value;
              if (name === 'custom:accountId') agg.accountId = value;
              if (name === 'custom:managerId') agg.managerId = value;
              if (name === 'custom:role') agg.role = value;
              return agg;
            },
            {},
          );
          // console.log('authenticated', authenticateUser)
          authenticateUser.cognitoUser = cognitoUser;
          authenticateUser.session = session;
          // console.log('session validity: ' + session.isValid());
          resolve(authenticateUser);
        });
      });
    } else {
      resolve({});
    }
  });
};

export const confirmPassword = async (
  { userPool }: { userPool: CognitoUserPool },
  {
    email,
    password,
    token,
  }: { email: string; password: string; token: string },
) => {
  const userData = {
    Username: email,
    Pool: userPool,
  };
  const cognitoUser = new CognitoUser(userData);
  return new Promise((resolve: any, reject: any) => {
    cognitoUser.confirmPassword(token, password, {
      onSuccess: resolve,
      onFailure: reject,
    });
  });
};

export const forgotPassword = async (
  { userPool }: { userPool: CognitoUserPool },
  { email }: { email: string },
) => {
  const userData = {
    Username: email,
    Pool: userPool,
  };
  const cognitoUser = new CognitoUser(userData);
  return new Promise((resolve, reject) => {
    cognitoUser.forgotPassword({
      onSuccess: (data) => {
        // console.log('data',data)
        resolve(data);
      },
      onFailure: reject,
    });
  });
};

export const loginUser = async (
  { userPool }: { userPool: CognitoUserPool },
  { email, password }: LoginOptions,
): Promise<AuthenticatedUser> => {
  return new Promise<AuthenticatedUser>((resolve, reject) => {
    const authenticationData = {
      Username: email,
      Password: password,
    };
    const authenticationDetails = new AuthenticationDetails(authenticationData);
    const userData = {
      Username: email,
      Pool: userPool,
    };
    const cognitoUser = new CognitoUser(userData);

    const handler = cognitoUser.authenticateUser(authenticationDetails, {
      mfaRequired: function (codeDeliveryDetails) {
        // MFA is required to complete user authentication.
        // Get the code from user and call
        //cognitoUser.sendMFACode(mfaCode, this)
      },

      newPasswordRequired: function (userAttributes, requiredAttributes) {
        // User was signed up by an admin and must provide new
        // password and required attributes, if any, to complete
        // authentication.

        // userAttributes: object, which is the user's current profile. It will list all attributes that are associated with the user.
        // Required attributes according to schema, which don’t have any values yet, will have blank values.
        // requiredAttributes: list of attributes that must be set by the user along with new password to complete the sign-in.
        const user: AuthenticatedUser = {
          cognitoUser,
          setNewPassword: async (newPassword: string, attributesData = {}) => {
            return new Promise<AuthenticatedUser>((resolve, reject) => {
              cognitoUser.completeNewPasswordChallenge(
                newPassword,
                attributesData,
                {
                  onSuccess: (session) => resolve({ cognitoUser, session }),
                  onFailure: (err) => reject(err),
                },
              );
            });
          },
        };
        // console.log('new password required', user)
        return resolve(user);

        // Get these details and call
        // newPassword: password that user has given
        // attributesData: object with key as attribute name and value that the user has given.
      },
      onSuccess: (session: CognitoUserSession) =>
        resolve({ cognitoUser, session }),
      onFailure: (err) => reject(err),
    });
  });
};
export let Auth: AuthClient;
export const initAuth = (config: Config) => {
  Auth = new AuthClient(config);
  return Auth;
};
